import { Fragment, useEffect, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import DataTable from 'react-data-table-component';
import Select from 'react-select';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { editarBreadcrumb } from '../../actions/breadcrumb';
import {
    buscarFacturas,
    filtrarFacturas,
    setFiltrosFacturas,
    limpiarFiltrosFacturas,
    eliminarFactura,
    devolverFactura
} from '../../actions/facturas';
import { buscarProveedores } from '../../actions/proveedores';

import ModalCambiarStatusFactura from './ModalCambiarStatusFactura';
import VerFacturaModal from './VerFacturaModal';
import EliminarFacturaModal from './modals/EliminarFacturaModal';
import DevolverFacturaModal from './modals/DevolverFacturaModal';

const formatDate = (value) => {
    if (!value) return 'Sin definir';
    const d = new Date(value);
    if (isNaN(d.getTime())) return value;
    return d.toLocaleDateString('es-MX');
};

const formatMoney = (value) => {
    const n = Number(value);
    if (Number.isNaN(n)) return 'Sin definir';
    return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(n);
};

const Facturas = () => {
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(editarBreadcrumb([{ activo: true, nombre: 'Facturas', path: '/facturas' }]));
    }, [dispatch]);

    const { filtrado, filtro_activo, filtros } = useSelector(state => state.facturas);
    const { listado: listado_proveedores } = useSelector(state => state.proveedores);

    const [textoFiltro, setTextoFiltro] = useState('');
    const [modalFiltros, setModalFiltros] = useState(false);

    const [proveedorSel, setProveedorSel] = useState(null);
    const [fechaInicio, setFechaInicio] = useState('');
    const [fechaFin, setFechaFin] = useState('');

    const [facturaEliminar, setFacturaEliminar] = useState(null);
    const [facturaDevolver, setFacturaDevolver] = useState(null);
    const [facturaEstado, setFacturaEstado] = useState(null);
    const [facturaVer, setFacturaVer] = useState(null);

    useEffect(() => {
        dispatch(buscarFacturas({}));
        dispatch(buscarProveedores({}));
    }, [dispatch]);

    useEffect(() => {
        if (!filtro_activo) return;

        setFechaInicio(filtros?.fecha_inicio || '');
        setFechaFin(filtros?.fecha_fin || '');

        if (listado_proveedores?.length && filtros?.id_proveedor) {
            const match = listado_proveedores.find(p => (p?._id + '') === (filtros.id_proveedor + ''));
            if (match) setProveedorSel({ value: match._id, label: match.nombre || match.razon_social || 'Proveedor' });
        }
    }, [filtro_activo, filtros, listado_proveedores]);

    const opcionesProveedores = useMemo(() => {
        return (listado_proveedores || []).map(p => ({
            value: p._id,
            label: p.nombre || p.razon_social || p.codigo || 'Proveedor',
        }));
    }, [listado_proveedores]);

    const renderBadgeStatus = (status) => {
        const s = Number(status);
        if (s === 0) return <span className="badge bg-warning text-dark">Pendientes</span>;
        if (s === 1) return <span className="badge text-dark" style={{ backgroundColor: '#fd7e14' }}>Parcialmente recibida</span>;
        if (s === 2) return <span className="badge bg-success">Recibidos</span>;
        if (s === 3) return <span className="badge bg-danger">Cancelada</span>;
        return <span className="badge bg-secondary">Sin definir</span>;
    };

    const refrescarTabla = async () => {
        const filtrosEnviar = (filtro_activo && filtros)
            ? filtros
            : { id_proveedor: '', fecha_inicio: '', fecha_fin: '' };

        await dispatch(buscarFacturas(filtrosEnviar));
    };

    const onConfirmEliminar = async () => {
        if (!facturaEliminar?._id) return;
        const ok = await dispatch(eliminarFactura(facturaEliminar._id));
        if (ok) {
            setFacturaEliminar(null);
            await refrescarTabla();
        }
    };

    const onConfirmDevolverCompleta = async () => {
        if (!facturaDevolver?._id) return;
        const ok = await dispatch(devolverFactura(facturaDevolver._id));
        if (ok) {
            setFacturaDevolver(null);
            await refrescarTabla();
        }
    };

    const columns = useMemo(() => ([
        {
            name: 'Número',
            selector: row => row?.numero || 'Sin definir',
            sortable: true,
            wrap: true,
            grow: 1,
        },
        {
            name: 'Proveedor',
            selector: row => row?.proveedor?.nombre || 'Sin definir',
            sortable: true,
            wrap: true,
            grow: 2,
        },
        {
            name: 'Fecha',
            selector: row => formatDate(row?.fecha),
            sortable: true,
            wrap: true,
            grow: 1,
        },
        {
            name: 'Total',
            selector: row => formatMoney(row?.monto_real ?? row?.monto),
            sortable: true,
            right: true,
            wrap: true,
            grow: 1,
        },
        {
            name: '# Productos',
            selector: row => (row?.productos ?? 0),
            sortable: true,
            right: true,
            wrap: true,
            width: '130px',
        },
        {
            name: 'Status',
            sortable: true,
            width: '140px',
            cell: row => renderBadgeStatus(row?.status),
        },
        {
            name: 'Opciones',
            width: '190px',
            cell: row => {
                const status = Number(row?.status);
                const puedeCambiarStatus = status === 0 || status === 1;
                const puedeEditarEliminar = status === 0;
                const puedeDevolver = status === 1 || status === 2;

                return (
                    <div className="d-flex align-items-center gap-2">
                        <button
                            type="button"
                            className="btn btn-sm btn-link px-2"
                            title="Ver factura"
                            onClick={() => setFacturaVer(row)}
                        >
                            <i className="fas fa-eye text-primary" />
                        </button>

                        {puedeCambiarStatus && (
                            <button
                                type="button"
                                className="btn btn-sm btn-link px-2"
                                title="Cambiar status"
                                onClick={() => setFacturaEstado(row)}
                            >
                                <i className="fas fa-receipt text-info" />
                            </button>
                        )}

                        {puedeEditarEliminar && (
                            <Fragment>
                                <Link
                                    to={`/facturas/editar/${row._id}`}
                                    className="btn btn-sm btn-link px-2"
                                    title="Editar"
                                >
                                    <i className="fa fa-edit text-warning" />
                                </Link>

                                <button
                                    type="button"
                                    className="btn btn-sm btn-link px-2"
                                    title="Eliminar"
                                    onClick={() => setFacturaEliminar(row)}
                                >
                                    <i className="fas fa-trash text-danger" />
                                </button>
                            </Fragment>
                        )}

                        {puedeDevolver && (
                            <button
                                type="button"
                                className="btn btn-sm btn-link px-2"
                                title="Devolución"
                                onClick={() => setFacturaDevolver(row)}
                            >
                                <i className="fas fa-rotate-left" style={{ color: '#fd7e14' }} />
                            </button>
                        )}
                    </div>
                );
            },
        },
    ]), []);

    const paginationOptions = {
        rowsPerPageText: 'Filas por página',
        rangeSeparatorText: 'de',
        selectAllRowsItem: true,
        selectAllRowsItemText: 'Todos',
    };

    const onChangeFiltroTabla = (e) => {
        const val = e.target.value;
        setTextoFiltro(val);
        dispatch(filtrarFacturas(val));
    };

    const abrirFiltros = () => setModalFiltros(true);
    const cerrarFiltros = () => setModalFiltros(false);

    const onBuscarAvanzado = async () => {
        const filtrosEnviar = {
            id_proveedor: proveedorSel?.value || '',
            fecha_inicio: fechaInicio || '',
            fecha_fin: fechaFin || '',
        };

        await dispatch(setFiltrosFacturas(filtrosEnviar));
        await dispatch(buscarFacturas(filtrosEnviar));
        cerrarFiltros();
    };

    const onLimpiarAvanzado = async () => {
        setProveedorSel(null);
        setFechaInicio('');
        setFechaFin('');
        setTextoFiltro('');

        await dispatch(limpiarFiltrosFacturas());
        await dispatch(buscarFacturas({}));
        await dispatch(filtrarFacturas(''));
    };

    return (
        <Fragment>
            <div className="row">
                <div className="col-md-10 offset-md-1 pb-4">
                    <h3 className="my-5">Facturas</h3>

                    <div className="d-flex justify-content-end mb-3">
                        <div className="d-flex align-items-center gap-2 w-100 justify-content-end">
                            <div style={{ minWidth: 280 }}>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Buscar en la tabla..."
                                    value={textoFiltro}
                                    onChange={onChangeFiltroTabla}
                                />
                            </div>

                            <button type="button" className="btn btn-outline-secondary" onClick={abrirFiltros}>
                                <i className="feather icon-filter" /> Filtros avanzados
                            </button>

                            <Link to="/facturas/nueva" className="btn btn-primary">
                                <i className="feather icon-plus" /> Nueva factura
                            </Link>
                        </div>
                    </div>

                    <div className="mt-2">
                        <DataTable
                            columns={columns}
                            data={filtrado || []}
                            pagination
                            paginationComponentOptions={paginationOptions}
                            highlightOnHover
                            striped
                            responsive
                            persistTableHead
                        />
                    </div>
                </div>
            </div>

            <Modal show={modalFiltros} onHide={cerrarFiltros} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Filtros avanzados</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <div className="mb-3">
                        <Select
                            options={opcionesProveedores}
                            value={proveedorSel}
                            onChange={setProveedorSel}
                            isClearable
                            placeholder="Proveedor..."
                            noOptionsMessage={() => 'Sin resultados'}
                        />
                    </div>

                    <div className="row">
                        <div className="col-12 col-md-6 mb-3">
                            <input
                                type="date"
                                className="form-control"
                                value={fechaInicio}
                                onChange={(e) => setFechaInicio(e.target.value)}
                                placeholder="Fecha inicio"
                            />
                        </div>

                        <div className="col-12 col-md-6 mb-3">
                            <input
                                type="date"
                                className="form-control"
                                value={fechaFin}
                                onChange={(e) => setFechaFin(e.target.value)}
                                placeholder="Fecha fin"
                            />
                        </div>
                    </div>

                    {filtro_activo && (
                        <div className="alert alert-info mb-0">
                            Hay filtros activos aplicados.
                        </div>
                    )}
                </Modal.Body>

                <Modal.Footer>
                    <button type="button" className="btn btn-primary" onClick={onBuscarAvanzado}>
                        Buscar
                    </button>

                    <button
                        type="button"
                        className="btn btn-warning"
                        onClick={onLimpiarAvanzado}
                        disabled={!filtro_activo && !proveedorSel && !fechaInicio && !fechaFin}
                    >
                        Limpiar
                    </button>

                    <button type="button" className="btn btn-secondary" onClick={cerrarFiltros}>
                        Cerrar
                    </button>
                </Modal.Footer>
            </Modal>

            <VerFacturaModal
                show={!!facturaVer}
                facturaId={facturaVer?._id}
                onHide={() => setFacturaVer(null)}
            />

            <ModalCambiarStatusFactura
                show={!!facturaEstado}
                factura={facturaEstado}
                onClose={() => setFacturaEstado(null)}
                onSuccess={async () => {
                    setFacturaEstado(null);
                    await refrescarTabla();
                }}
            />

            <EliminarFacturaModal
                show={!!facturaEliminar}
                factura={facturaEliminar}
                onCancel={() => setFacturaEliminar(null)}
                onConfirm={onConfirmEliminar}
            />

            <DevolverFacturaModal
                show={!!facturaDevolver}
                factura={facturaDevolver}
                onCancel={() => setFacturaDevolver(null)}
                onConfirm={onConfirmDevolverCompleta}
                onSuccess={async () => {
                    setFacturaDevolver(null);
                    await refrescarTabla();
                }}
            />
        </Fragment>
    );
};

export default Facturas;
